import { useMemo, useState } from 'react';
import {
  BookOpen,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Edit3,
  Save,
  X,
  Search,
  FileText,
  Lightbulb,
  Wrench,
  ClipboardList,
  Users,
} from 'lucide-react';
import { DesktopMonthCalendar } from './DesktopMonthCalendar';
import type { DiarioClasse, UpdateDiarioClasseInput } from '../../api/hooks/useDiarioClasse';
import { useHojeReferencia } from '../../api/hooks/useSimClock';
import './diario-professor-desktop.css';
import './professor-dashboard-desktop.css';

function formatDateSafe(dateStr: string, format?: Intl.DateTimeFormatOptions): string {
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const date = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10), 12, 0, 0);
      if (!isNaN(date.getTime())) return date.toLocaleDateString('pt-BR', format);
    }
  } catch { /* ignore */ }
  return dateStr;
}

function ResumoChamada({ chamada }: { chamada?: DiarioClasse['chamada'] }) {
  if (!chamada) return null;
  const total = chamada.totalAlunos ?? 0;
  const presentes = chamada.totalPresentes ?? 0;
  const faltas = chamada.totalAusentes ?? 0;
  if (total === 0 && presentes === 0 && faltas === 0) return null;

  return (
    <div className="diar-desk-chamada-row">
      <span className="diar-desk-chamada-pill diar-desk-chamada-pill--total">
        <Users size={9} style={{ marginRight: 2, verticalAlign: -1 }} />
        {total} alunos
      </span>
      <span className="diar-desk-chamada-pill diar-desk-chamada-pill--ok">{presentes} presentes</span>
      {faltas > 0 && (
        <span className="diar-desk-chamada-pill diar-desk-chamada-pill--bad">{faltas} faltas</span>
      )}
    </div>
  );
}

export interface DiarioProfessorDesktopProps {
  professorId: string;
  diarios: DiarioClasse[];
  isLoading: boolean;
  dataSelecionada: string;
  setDataSelecionada: (iso: string) => void;
  busca: string;
  setBusca: (v: string) => void;
  filtroTurma: string;
  setFiltroTurma: (v: string) => void;
  filtroMateria: string;
  setFiltroMateria: (v: string) => void;
  filtroPeriodo: 'semana' | 'mes' | 'bimestre' | 'todos';
  setFiltroPeriodo: (v: 'semana' | 'mes' | 'bimestre' | 'todos') => void;
  turmasOpcoes: { id: string; nome: string }[];
  materiasOpcoes: { id: string; nome: string }[];
  countByDay: Record<string, number>;
  onSalvar: (input: UpdateDiarioClasseInput & { id: string }) => void;
  salvandoEdit: boolean;
}

export function DiarioProfessorDesktop({
  professorId,
  diarios,
  isLoading,
  dataSelecionada,
  setDataSelecionada,
  busca,
  setBusca,
  filtroTurma,
  setFiltroTurma,
  filtroMateria,
  setFiltroMateria,
  filtroPeriodo,
  setFiltroPeriodo,
  turmasOpcoes,
  materiasOpcoes,
  countByDay,
  onSalvar,
  salvandoEdit,
}: DiarioProfessorDesktopProps) {
  const { hojeStr } = useHojeReferencia();
  const hoje = hojeStr || dataSelecionada;
  const isHoje = !!hoje && dataSelecionada === hoje;

  const diariosFiltrados = useMemo(() => {
    let list = diarios;
    if (busca.trim()) {
      const termo = busca.toLowerCase();
      list = list.filter(
        (d) =>
          d.conteudoMinistrado?.toLowerCase().includes(termo) ||
          d.metodologia?.toLowerCase().includes(termo) ||
          d.observacoes?.toLowerCase().includes(termo) ||
          d.turma?.nome?.toLowerCase().includes(termo) ||
          d.materia?.nome?.toLowerCase().includes(termo),
      );
    }
    return list;
  }, [diarios, busca]);

  const diariosDoDia = useMemo(
    () =>
      diariosFiltrados.filter(
        (d) => (d.dataReferencia?.split('T')[0] || '') === dataSelecionada,
      ),
    [diariosFiltrados, dataSelecionada],
  );

  const totalRegistros = diariosFiltrados.length;
  const registrosNoDia = diariosDoDia.length;
  const objetivosCumpridos = diariosFiltrados.filter((d) => d.objetivosCumpridos !== false).length;
  const turmasCount = turmasOpcoes.length;

  const dataLabel = formatDateSafe(dataSelecionada, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const totalPeriodo = Object.values(countByDay).reduce((a, b) => a + b, 0);
  const calendarSubtitle = isLoading
    ? 'Carregando…'
    : `${totalPeriodo} registro${totalPeriodo !== 1 ? 's' : ''} no período`;

  const [expandidoId, setExpandidoId] = useState<string | null>(null);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [editDados, setEditDados] = useState<UpdateDiarioClasseInput & { id: string }>({ id: '' });

  const iniciarEdicao = (d: DiarioClasse) => {
    if (d.editavel === false) return;
    setEditandoId(d.id);
    setExpandidoId(d.id);
    setEditDados({
      id: d.id,
      conteudoMinistrado: d.conteudoMinistrado || '',
      metodologia: d.metodologia || '',
      recursosUtilizados: d.recursosUtilizados || '',
      observacoes: d.observacoes || '',
      objetivosCumpridos: d.objetivosCumpridos ?? true,
    });
  };

  const cancelarEdicao = () => {
    setEditandoId(null);
  };

  const salvarEdicao = () => {
    if (!editDados.id) return;
    onSalvar(editDados);
  };

  const toggleItem = (id: string) => {
    if (editandoId === id) return;
    setExpandidoId((prev) => (prev === id ? null : id));
  };

  const dayPanel = (
    <div className="diar-desk-day-panel">
      <header className="diar-desk-day-head">
        <div>
          <p className="diar-desk-day-kicker">Registros do dia</p>
          <p className="diar-desk-day-date">
            {new Date(dataSelecionada + 'T12:00:00').toLocaleDateString('pt-BR', {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
            })}
          </p>
          {!isHoje && <p className="diar-desk-day-meta">Consulta de outro dia</p>}
        </div>
        {!isHoje && (
          <button
            type="button"
            className="dash-desk-action-btn diar-desk-day-hoje-btn"
            onClick={() => setDataSelecionada(hoje)}
          >
            Hoje
          </button>
        )}
      </header>

      {isLoading ? (
        <p className="diar-desk-day-empty">Carregando registros…</p>
      ) : diariosDoDia.length === 0 ? (
        <p className="diar-desk-day-empty">
          {busca.trim()
            ? 'Nenhum registro neste dia para a busca atual.'
            : 'Sem diário neste dia. Os registros aparecem após concluir a chamada.'}
        </p>
      ) : (
        <ul className="diar-desk-list">
          {diariosDoDia.map((d) => {
            const isExpanded = expandidoId === d.id;
            const isEditing = editandoId === d.id;
            const ok = d.objetivosCumpridos !== false;

            return (
              <li key={d.id} className="diar-desk-li">
                <div
                  className={[
                    'diar-desk-item',
                    isExpanded ? 'diar-desk-item--active' : '',
                    d.editavel === false ? 'diar-desk-item--readonly' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className="diar-desk-item-top" onClick={() => toggleItem(d.id)} role="presentation">
                    <div
                      className={`diar-desk-item-icon ${ok ? 'diar-desk-item-icon--ok' : 'diar-desk-item-icon--fail'}`}
                    >
                      {ok ? (
                        <CheckCircle2 size={14} style={{ color: '#16a34a' }} />
                      ) : (
                        <XCircle size={14} style={{ color: '#dc2626' }} />
                      )}
                    </div>
                    <div className="diar-desk-item-main">
                      <p className="diar-desk-item-title">
                        {d.conteudoMinistrado || 'Sem conteúdo registrado'}
                      </p>
                      <div className="diar-desk-item-tags">
                        {d.turma?.nome && (
                          <span className="diar-desk-tag diar-desk-tag--turma">{d.turma.nome}</span>
                        )}
                        {d.materia?.nome && (
                          <span className="diar-desk-tag diar-desk-tag--materia">{d.materia.nome}</span>
                        )}
                      </div>
                      <ResumoChamada chamada={d.chamada} />
                    </div>
                    <div className="diar-desk-item-actions">
                      {!isEditing && d.editavel !== false && (
                        <button
                          type="button"
                          className="diar-desk-edit-btn"
                          title="Editar"
                          onClick={(e) => {
                            e.stopPropagation();
                            iniciarEdicao(d);
                          }}
                        >
                          <Edit3 size={12} />
                        </button>
                      )}
                      {!isEditing && d.editavel === false && (
                        <span className="diar-desk-readonly">Somente leitura</span>
                      )}
                    </div>
                  </div>

                  {isExpanded && !isEditing && (
                    <div className="diar-desk-detail">
                      {d.metodologia && (
                        <div className="diar-desk-field">
                          <div>
                            <div className="diar-desk-field-label">Metodologia</div>
                            {d.metodologia}
                          </div>
                        </div>
                      )}
                      {d.recursosUtilizados && (
                        <div className="diar-desk-field">
                          <div>
                            <div className="diar-desk-field-label">Recursos</div>
                            {d.recursosUtilizados}
                          </div>
                        </div>
                      )}
                      {d.observacoes && (
                        <div className="diar-desk-field">
                          <div>
                            <div className="diar-desk-field-label">Observações</div>
                            {d.observacoes}
                          </div>
                        </div>
                      )}
                      {d.chamada && (d.chamada.totalAlunos ?? 0) > 0 && (
                        <div className="diar-desk-field">
                          <ClipboardList size={11} style={{ flexShrink: 0, marginTop: 2, color: '#2563eb' }} />
                          <div>
                            <div className="diar-desk-field-label">Chamada</div>
                            {d.chamada.totalAlunos} alunos · {d.chamada.totalPresentes ?? 0} presentes ·{' '}
                            {d.chamada.totalAusentes ?? 0} faltas
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {isEditing && (
                    <div className="diar-desk-edit-body">
                      <div>
                        <label>
                          <FileText size={10} /> Conteúdo *
                        </label>
                        <textarea
                          value={editDados.conteudoMinistrado || ''}
                          onChange={(e) =>
                            setEditDados((p) => ({ ...p, conteudoMinistrado: e.target.value }))
                          }
                          rows={2}
                        />
                      </div>
                      <div>
                        <label>
                          <Lightbulb size={10} /> Metodologia
                        </label>
                        <input
                          type="text"
                          value={editDados.metodologia || ''}
                          onChange={(e) => setEditDados((p) => ({ ...p, metodologia: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label>
                          <Wrench size={10} /> Recursos
                        </label>
                        <input
                          type="text"
                          value={editDados.recursosUtilizados || ''}
                          onChange={(e) =>
                            setEditDados((p) => ({ ...p, recursosUtilizados: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <label>Observações</label>
                        <textarea
                          value={editDados.observacoes || ''}
                          onChange={(e) => setEditDados((p) => ({ ...p, observacoes: e.target.value }))}
                          rows={2}
                        />
                      </div>
                      <label
                        style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                      >
                        <input
                          type="checkbox"
                          checked={!!editDados.objetivosCumpridos}
                          onChange={(e) =>
                            setEditDados((p) => ({ ...p, objetivosCumpridos: e.target.checked }))
                          }
                        />
                        Objetivos cumpridos
                      </label>
                      <div className="diar-desk-edit-actions">
                        <button type="button" onClick={cancelarEdicao} style={{ border: '1px solid #d1d5db', background: 'transparent' }}>
                          <X size={10} /> Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={salvarEdicao}
                          disabled={salvandoEdit}
                          style={{
                            border: 'none',
                            background: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
                            color: '#fff',
                            opacity: salvandoEdit ? 0.6 : 1,
                          }}
                        >
                          <Save size={10} /> {salvandoEdit ? 'Salvando…' : 'Salvar'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return (
    <div className="admin-page text-[13px] leading-snug dash-desk-page diar-desk-page">
      <div className="dash-desk-root diar-desk-root">
        <div className="dash-desk-top diar-desk-top">
          <div className="dash-desk-greeting">
            <h1 className="diar-desk-date-title">{dataLabel}</h1>
            {!isHoje && <p className="diar-desk-date-sub">Consulta de outro dia · histórico</p>}
          </div>
          <div className="dash-desk-actions">
            {!isHoje && (
              <button
                type="button"
                className="dash-desk-action-btn"
                onClick={() => setDataSelecionada(hoje)}
              >
                <CalendarDays size={14} /> Hoje
              </button>
            )}
          </div>
        </div>

        <div className="diar-desk-metrics">
          <div className="dash-desk-kpi">
            <div className="dash-desk-kpi-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
              <BookOpen size={15} />
            </div>
            <div>
              <div className="dash-desk-kpi-val">{totalRegistros}</div>
              <div className="dash-desk-kpi-lbl">Total no período</div>
            </div>
          </div>
          <div className="dash-desk-kpi">
            <div
              className="dash-desk-kpi-icon"
              style={{
                background: registrosNoDia ? '#f0fdf4' : '#fffbeb',
                color: registrosNoDia ? '#059669' : '#d97706',
              }}
            >
              <CalendarDays size={15} />
            </div>
            <div>
              <div className="dash-desk-kpi-val">{registrosNoDia}</div>
              <div className="dash-desk-kpi-lbl">No dia</div>
            </div>
          </div>
          <div className="dash-desk-kpi">
            <div className="dash-desk-kpi-icon" style={{ background: '#f0fdf4', color: '#059669' }}>
              <CheckCircle2 size={15} />
            </div>
            <div>
              <div className="dash-desk-kpi-val">{objetivosCumpridos}</div>
              <div className="dash-desk-kpi-lbl">Objetivos OK</div>
            </div>
          </div>
          <div className="dash-desk-kpi">
            <div className="dash-desk-kpi-icon" style={{ background: '#ede9fe', color: '#7c3aed' }}>
              <BookOpen size={15} />
            </div>
            <div>
              <div className="dash-desk-kpi-val">{turmasCount}</div>
              <div className="dash-desk-kpi-lbl">Turmas</div>
            </div>
          </div>
        </div>

        <div className="diar-desk-toolbar">
          <div className="diar-desk-search-wrap">
            <Search size={14} />
            <input
              type="text"
              className="diar-desk-search"
              placeholder="Buscar conteúdo, turma, matéria…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <div className="diar-desk-filters">
            <select value={filtroTurma} onChange={(e) => setFiltroTurma(e.target.value)}>
              <option value="">Todas turmas</option>
              {turmasOpcoes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome}
                </option>
              ))}
            </select>
            <select value={filtroMateria} onChange={(e) => setFiltroMateria(e.target.value)}>
              <option value="">Todas matérias</option>
              {materiasOpcoes.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nome}
                </option>
              ))}
            </select>
            <select
              value={filtroPeriodo}
              onChange={(e) => setFiltroPeriodo(e.target.value as DiarioProfessorDesktopProps['filtroPeriodo'])}
            >
              <option value="semana">Semana</option>
              <option value="mes">Mês</option>
              <option value="bimestre">Bimestre</option>
              <option value="todos">Todos</option>
            </select>
          </div>
        </div>

        <div className="diar-desk-cal-wrap">
          <DesktopMonthCalendar
            professorId={professorId}
            pickerOnly
            className="dash-desk-cal--diario"
            selectedIso={dataSelecionada}
            onSelectIso={setDataSelecionada}
            hojeIso={hoje}
            countByDay={countByDay}
            calendarTitle="Calendário"
            calendarSubtitle={calendarSubtitle}
            countUnit={{ one: 'reg.', many: 'reg.' }}
            legendVariant="diario"
            dayPanel={dayPanel}
          />
        </div>
      </div>
    </div>
  );
}
